







import Enumerable from "linq";
import * as Moment from "moment";
import { Component, Mixins, Prop, Emit, Watch } from "vue-property-decorator";
import { DomainRegistry } from "../../../../domain";
import {
  Document,
  EditPhaseKinds,
  PatientProfileUpdater,
  DocumentRegistrationResult,
  IDocumentRegistrationResultRegisteredDetail,
  IDocumentRegistrationResultAlreadyUpdatedDetail,
  DocumentSummary,
  RequestedObject,
  RegisterAlertKinds,
  DocumentAttributes,
  DocumentCollaborationSupplement,
} from "../../../../domain/document";
import { IBehaviorDocumentEditorManagementConfiguration, IBehaviorDocumentEditorValidatorsConfiguration } from "../../../../domain/configuration";
import { EditingDocument } from "..";
import { EditTimeApplicator, HisInformationApplicator, UserDefinitionLinkValueApplicator } from "../helpers";
import DocumentValidator from "./DocumentValidator.vue";
import { DepartmentGroup } from "../../../../domain/department";
import { PostRegistrationApplicationLauncher } from "./PostRegistrationApplicationLauncher";
import { User } from "../../../../domain/user";
import { IUserListSelectorDialogCondition } from "../../../user/dialogs";
import RequestedDocumentProcessor from "../../helpers/RequestedDocumentProcessor.vue";

enum MessageBoxCloseAction {
  Update,
  New,
  Cancel,
}

@Component({
  components: {
    RequestedDocumentProcessor,
  }
})
export default class DocumentRegisterer extends Mixins() {
  $refs!: {
    processor: RequestedDocumentProcessor,
  };

  @Prop()
  document: Document;

  @Prop()
  validator: DocumentValidator;

  @Prop()
  register: (source: Document, isTemporary: boolean, isPublish: boolean) => Promise<DocumentRegistrationResult>;

  @Prop()
  postRegistrationProcess: (id: number) => Promise<void>;

  @Prop()
  systemKey: string;

  @Prop()
  linkKey: string;

  @Prop({ default: false })
  shouldValidateExistingLinkedDocument: boolean;

  @Prop({ default: false })
  shouldValidateAllPages: boolean;

  data(): any {
    return {
      isExternalScriptProcessing: false,
    };
  }
  isExternalScriptProcessing: boolean;
  
  /**
   * UIの状態に依存しないガードするための登録処理中フラグ
   * 登録中のローディング表示等のUIのガードはこのコンポーネントを利用する側で実装しているが、
   * ローディング表示によるガードが間に合わないケースがあるため、
   * 登録処理中に別の登録が開始されないように対策している
   */
  private _isDocumentRegistering: boolean;
  private _isDocumentRegistered: boolean;

  @Watch("document")
  onDocumentUpdated(): void {
    // 文書登録完了後にローディングが解除される一瞬で登録可能になってしまうタイミングがあるので対策している
    // 念のため文書更新があればフラグクリアするようにしている
    // 根本的にはローディングが解除されるタイミングと登録完了通知が完了するタイミングを調整する必要がある
    this._isDocumentRegistered = false;
  }

  @Emit()
  documentRegistered(id: number, destinationRoute: any, isTemporary?: boolean, archiveId?: number): any {}

  get validatorsConfiguration(): IBehaviorDocumentEditorValidatorsConfiguration {
    return this.$configuration.behavior.document.editor.validator;
  }

  public async registerDocument(shouldValidate: boolean): Promise<number> {
    if (!this.verifyDocumentRegistering()) {
      return null;
    }
    try {
      this._isDocumentRegistering = true;
      if (!await this._attributesValidate()) {
        return null;
      }
      if (!await this.verifyBeforeRegister()) {
        return null;
      }
      this.$logger.info("登録を開始します。");
      const id = await this._registerDocument(shouldValidate, false);
      if (!id) { return id; }
      await this._launchPostRegistrationApplication(this.document.attributes, id, false, false);
      this._isDocumentRegistered = true;
      return id;
    } finally {
      this._isDocumentRegistering = false;
    }
  }

  private async _registerDocument(shouldValidate: boolean, isPublish: boolean): Promise<number> {
    if (this._shouldConfirmWhetherRegisterWithoutBrowsingAllPages()) {
      if (!await this.confirmWhetherRegisterWithoutBrowsingAllPages()) {
        return null;
      }
    }
    let requestedObjects: RequestedObject[] = [];
    if (shouldValidate) {
      const result = await this.validator.validate(true);
      if (!result.isOk) {
        this.$warn("登録中止", "登録する文書に問題があったため登録を中止します。警告内容を確認してください。");
        return null;
      }
      requestedObjects = result.requestedObjects;
    }
    try {
      const source = this.document.clone();
      source.attributes.temporaryNote = "";

      const shouldRegister = await this._updateCostSendNecessity(source)
        && await this._updatePatientProfile(source)
        && await this._applyHisInformations(source, EditPhaseKinds.Save)
        && await this._applyUserDefinitionLinkValue(source)
        && await this._applyCollaborationSupplement(source, false);
      if (!shouldRegister) {
        this.$success("登録", "登録をキャンセルしました。");
        return null;
      }
      await this.$refs.processor.applyRequestedValues(new EditingDocument(source), requestedObjects);
      this.applyTimeMeasurementRelatedValues(source);
      return await this._executeRegistration(source, false, isPublish);
    } catch (e) {
      // 利用側でメッセージを変えたいという要望があったので、メッセージはregisterで表示する役割分担に.
    }
  }

  private applyTimeMeasurementRelatedValues(source: Document) {
    const editingDocument = new EditingDocument(source);
    const applicator = new EditTimeApplicator(editingDocument, this.$configuration);
    applicator.apply();
  }

  /**
   * DMM承認連携のための承認者を選択
   */
  private async _applyCollaborationSupplement(source: Document, isTemporary: boolean): Promise<boolean> {
    // もともとセットはされていないはずだが、念のためクリアしている
    source.attributes.collaborationSupplement = null;
    
    const config = this.$configuration.behavior.document.editor.management;
    const isManageable = !!source.attributes.collaborationBehavior ? source.attributes.collaborationBehavior.isManageable : false;
    if (!this.document.attributes.behavior.shouldCreateCollaborationSupplement || !isManageable) {
      // 機能が無効になっているので選択不要
      return true;
    }
    if (config.shouldSkipWhenTemporaryRegistration && isTemporary) {
      return true;
    }
    const loginUser = this.$store.getters.user as User;
    if (!!loginUser.profession) {
      if (config.approver.professionCodes.some(code => code === loginUser.profession.id)) {
        // 承認者の職種なので選択不要
        return true;
      }
      if (config.restriction.professionCodes.length > 0 && config.restriction.professionCodes.every(code => code !== loginUser.profession.id)) {
        // 承認が必要な職種が指定されていて、なおかつその職種ではないので選択不要
        return true;
      }
    }
    const selectedUser = await this.selectActionRequestedUser(config);
    if (!selectedUser) {
      // 承認者の選択必須なのに選択されていないので登録処理中断する
      return false;
    }
    const supplement = new DocumentCollaborationSupplement();
    supplement.actionRequestedUserCode = selectedUser.id;
    supplement.actionRequestedUserName = selectedUser.name;
    source.attributes.collaborationSupplement = supplement;
    return true;
  }

  private async selectActionRequestedUser(config: IBehaviorDocumentEditorManagementConfiguration): Promise<User> {
    const defaultSelectedUser = this.$store.getters.attendingUser
      ? this.$store.getters.attendingUser
      : (this.$store.getters.orderUser ? this.$store.getters.orderUser : null);

    const autoSelectedUser = await this.selectActionRequestedUserAutomatically(config, defaultSelectedUser);
    if (!!autoSelectedUser) {
      this.$logger.debug("承認者を自動選択しました。");
      return autoSelectedUser;
    }
    const condition: IUserListSelectorDialogCondition = {
      name: "承認者",
      departmentId: null,
      defaultSelectedProfessionCodes: config.approver.professionCodes.concat(),
      shouldHideSelectLoginUserButton: true,
      shouldHideClearSelectionButton: true,
    };
    return this.$commonDialogs.user.userListSelector.selectOptionConditionally(condition, defaultSelectedUser);
  }

  private async selectActionRequestedUserAutomatically(config: IBehaviorDocumentEditorManagementConfiguration, defaultSelectedUser: User): Promise<User> {
    if (!config.approver.enableAutoSelection || !defaultSelectedUser) { return null; }

    // TODO: バックエンドの設定（期限切れ職員を取得する）によっては指示者・指導者の職種情報が格納されていない
    return (!!defaultSelectedUser.profession && config.approver.professionCodes.some(code => code === defaultSelectedUser.profession.id))
      ? defaultSelectedUser
      : null;
  }

  private _shouldConfirmWhetherRegisterWithoutBrowsingAllPages(): boolean {
    return this.shouldValidateAllPages
      && Enumerable.from(this.document.sections).any(s => !s.hasBeenBrowsed);
  }

  private async confirmWhetherRegisterWithoutBrowsingAllPages(): Promise<boolean> {
    const index = await this.$commonDialogs.confirmation.confirmRegisterCancel(
      null,
      ["閲覧していないページがあります。このまま書類を登録してもよろしいですか？"]
    );
    return (index === 0);
  }

  /**
   * 「初回登録時のみ印刷する」が指定されているページ（引換証）の印刷用
   */
  private _shouldPrintDocumentExchangePaper(isFirstNormalRegister: boolean): boolean {
    return (this.document.attributes.behavior.shouldPrintSpecifiedSectionAtFirstRegistration && isFirstNormalRegister)
      && this.document.sections.some(s => s.shouldPrintOnFirstRegistered);
  }

  private async _printDocumentExchangePaper(patientCode: string, documentId: number): Promise<void> {
    this.$logger.info("「初回登録時のみ印刷する」が指定されているページを印刷します。");
    const repository = DomainRegistry.document.getDocumentRepository();
    const reference = await repository.getDocumentSummary(patientCode, documentId);
    const printSource = repository.createDocumentExchangePaperPrintSource(patientCode, documentId, reference.edition);
    this.$printPdf(printSource.source);
  }

  private async _updateCostSendNecessity(source: Document): Promise<boolean> {
    this.$logger.info("コスト送信要否を更新します。");
    if (!source.attributes.shouldConfirmToSendCost) {
      this.$logger.info("コスト送信対象ではありません。");
      return true;
    }
    const index = await this.$commonDialogs.confirmation.show({
      message: ["この文書のコストを送信しますか？"],
      buttons: [
        {
          kind: "confirm",
          label: "送信する",
        },
        {
          kind: "danger",
          label: "送信しない",
        }
      ],
      cancellation: {
        isEnable: true,
        label: "登録を中止する",
      },
    });
    if (index === null || index === undefined) {
      // キャンセル
      this.$logger.info("コスト送信キャンセルされました。");
      return false;
    }
    source.attributes.shouldSendCost = (index === 0);
    this.$logger.info("コスト送信対象にします。");
    return true;
  }

  private async _updatePatientProfile(source: Document): Promise<boolean> {
    try {
      if (!this.$configuration.behavior.document.editor.patientProfile.isEnable) {
        // PatientProfileRepository内でも分岐している箇所あり、機能無効の場合は患者プロフィール自体を取得しない挙動にはなっている
        return true;
      }
      this.$logger.info("患者プロファイルを更新します。");
      // 登録のタイミングで患者プロフィールを自動更新する(挙動について設定あり)
      const updater = new PatientProfileUpdater(this.$store.getters.configuration.behavior.document.editor.patientProfile);
      updater.update(source);
      this.$logger.info("患者プロファイルを更新しました。");
      return true;
    } catch (e) {
      this.$error("患者プロフィール更新", "患者プロフィールの更新処理でエラーが発生しました。", e);
      return false;
    }
  }

  private async _applyHisInformations(source: Document, phase: EditPhaseKinds): Promise<boolean> {
    this.$logger.info("HIS情報を適用します。(" + phase + ")");
    const editingDocument = new EditingDocument(source);
    const hisApplicator = new HisInformationApplicator(editingDocument);
    hisApplicator.applyByProgress(
      source.hisInformation,
      phase,
      this.$store.getters.user,
      this.$store.getters.department,
      this.$store.getters.userAuthority.isOrderable ? this.$store.getters.user : source.attributes.orderUser,
      this.$store.getters.userAuthority.isOrderable ? this.$store.getters.user : source.attributes.attendingUser,
    );
    this.$logger.info("HIS情報適用による外部スクリプト実行します。");
    this.isExternalScriptProcessing = true;
    try {
      // newしたばかりのeditingDocumentなのでhasCancelをクリアする必要は無いが、あとで見たときに分かりやすいように明示的にクリアしている
      editingDocument.externalScriptRunner.hasCancel = false;
      await editingDocument.executeScriptInDocumentAsync(phase);
      const isSuccess = await editingDocument.executeStackedScriptsAsyncWithResult();
      if (!isSuccess) {
        this.$error("外部スクリプト実行", "外部スクリプトの実行中にエラーが発生しました。");
        return false;
      }
      if (editingDocument.externalScriptRunner.hasCancel) {
        this.$logger.info("外部スクリプトがキャンセルされています。登録を中断します。");
        return false;
      }
      this.$logger.info("HIS情報適用しました。");
      return true;
    } finally {
      this.isExternalScriptProcessing = false;
    }
  }

  private async _applyUserDefinitionLinkValue(source: Document): Promise<boolean> {
    this.$logger.info("ユーザ定義のリンク値を補正します。");
    try {
      const editingDocument = new EditingDocument(source);
      const applicator = new UserDefinitionLinkValueApplicator(editingDocument);
      await applicator.apply();
      this.$logger.info("ユーザ定義のリンク値を補正しました。");
      return true;
    } catch (e) {
      this.$error("ユーザ定義", "ユーザ定義のリンク値の補正処理中にエラーが発生しました。");
      return false;
    }
  }

  private async _executeRegistration(source: Document, isTemporary: boolean, isPublish: boolean): Promise<number> {
    if (!!this.shouldValidateExistingLinkedDocument) {
      const linkedDocumentId = await this.getLinkedDocumentId(this.systemKey, this.linkKey, this.document.attributes.patient.id);
      if (!!linkedDocumentId) {
        const response = await this.confirmToRegisteredLinkedDocument(linkedDocumentId, isTemporary);
        if (response === MessageBoxCloseAction.Cancel) {
          return null;
        }
      }
    }
    this.$logger.info("登録実行します.");
    const result = await this.register(source, isTemporary, isPublish);
    if (result === undefined) {
      this.$logger.info("登録がキャンセルされました.");
      return null;
    }
    if (!result.isRegistered) {
      const detail = result.detail as IDocumentRegistrationResultAlreadyUpdatedDetail;
      const isSingleEntryFormat = await this.isSingleEntryFormat(this.document.attributes.format.originalId);
      const response = await this.getConfirmResult(detail, isTemporary, isSingleEntryFormat);
      if (response !== MessageBoxCloseAction.Cancel) {
        return this.RetryRegistration(response, source, detail, isTemporary, isPublish);
      }
    } else {
      const detail = result.detail as IDocumentRegistrationResultRegisteredDetail;
      if (!detail.id) {
        this.$error(
          "登録失敗",
          "文書を登録できませんでした。",
        );
        this.$logger.warn("登録ハンドラがnullを返却しています.実装を確認してください.");
        return null;
      }
      if (this.postRegistrationProcess) {
        this.$logger.info("登録後処理を実行します.");
        await this.postRegistrationProcess(detail.id);
      }
      if (this._shouldPrintDocumentExchangePaper(detail.isFirstNormalRegister)) {
        await this._printDocumentExchangePaper(this.document.attributes.patient.id, detail.id);
      }
      // 登録完了通知はここでは出さない.(参照画面表示を早めるため参照画面で出す.)
      this.$logger.info("登録完了イベントを発行します.");
      this.documentRegistered(detail.id, undefined, isTemporary, detail.archiveId);
      return detail.id;
    }
  }

  private async isSingleEntryFormat(formatOriginalId: number): Promise<boolean> {
    if (!this.$configuration.behavior.document.editor.singleEntry.isEnable) { return false; }
    const repo = DomainRegistry.format.getFormatRepository();
    return repo.isSingleEntry(formatOriginalId);
  }

  public async registerDocumentAndPrint(shouldValidate: boolean, shouldSelectPrinter: boolean): Promise<number> {
    if (!this.verifyDocumentRegistering()) {
      return null;
    }
    try {
      this._isDocumentRegistering = true;
      if (!await this._attributesValidate()) {
        return null;
      }
      if (!await this.verifyBeforeRegister()) {
        return null;
      }
      this.$logger.info("登録を開始します。");
      const id = await this._registerDocument(shouldValidate, true);
      if (!id) { return id; }
      await this._printRegisteredDocument(this.document.attributes.patient.id, id, this.document.attributes.defaultCopies, shouldSelectPrinter);
      await this._launchPostRegistrationApplication(this.document.attributes, id, false, true);
      this._isDocumentRegistered = true;
      return id;
    } finally {
      this._isDocumentRegistering = false;
    }
  }

  private async _printRegisteredDocument(patientNo: string, id: number, copies: number, shouldSelectPrinter: boolean): Promise<void> {
    try {
      this.$logger.info("登録文書(" + patientNo + ", " + id + ", " + copies + ")を印刷します。");
      const repository = DomainRegistry.document.getDocumentRepository();
      const reference = await repository.getDocumentSummary(patientNo, id);
      // TODO: hasWatermarkの端末設定（東大では確定登録ではないときに端末設定次第で透かしも印刷させる）
      reference.printSource.withCommitAction = this.document.attributes.behavior.isCommitPhase;
      this.$printPdf(reference.printSource.source, copies, shouldSelectPrinter);
      this.$logger.info("印刷が完了しました。");
    } catch (e) {
      this.$error("印刷エラー", "印刷処理中にエラーが発生しました。", e);
    }
  }

  public async registerDocumentAsTemporary(shouldRestrictDocumentNote: boolean, shouldValidate: boolean): Promise<number> {
    if (!this.verifyDocumentRegistering()) {
      return null;
    }
    try {
      this._isDocumentRegistering = true;
      if (!await this._attributesValidate()) {
        return null;
      }
      if (!await this.verifyBeforeRegister()) {
        return null;
      }
      this.$logger.info("一時保存を開始します。");
      const id = await this._registerDocumentAsTemporary(shouldRestrictDocumentNote, shouldValidate);
      if (!id) { return id; }
      await this._launchPostRegistrationApplication(this.document.attributes, id, true, false);
      this._isDocumentRegistered = true;
      return id;
    } catch (e) {
      // 利用側でメッセージを変えたいという要望があったので、メッセージはregisterで表示する役割分担に.
    } finally {
      this._isDocumentRegistering = false;
    }
  }

  public async registerDocumentAsTemporaryForcibly(): Promise<number> {
    // 文書登録処理中に重複して登録処理を進めてしてしまった場合でもAPI側で競合判定してくれるはず
    // 新規文書編集の場合は重複してしまうことがあるかもだが、消えてしまうよりは重複したほうがましなので、
    // ここではverifyDocumentRegistering()によるチェックはしない
    // 一時保存メモのダイアログを表示しているときは表示が微妙にはなる
    try {
      this._isDocumentRegistering = true;
      this.$logger.info("強制一時保存を開始します。");
      if (!this.document.attributes.department) {
        // 基本的に空になっていることは無いはず
        this.document.attributes.department = this.$store.getters.department;
      }
      const id = await this._registerDocumentAsTemporary(true, false);
      if (!id) { return id; }
      await this._launchPostRegistrationApplication(this.document.attributes, id, true, false);
      this._isDocumentRegistered = true;
      return id;
    } catch (e) {
      // 利用側でメッセージを変えたいという要望があったので、メッセージはregisterで表示する役割分担に.
    } finally {
      this._isDocumentRegistering = false;
    }
  }

  public async registerDocumentAsTemporaryAndPrint(shouldRestrictDocumentNote: boolean, shouldValidate: boolean, hasWatermark: boolean): Promise<number> {
    if (!this.verifyDocumentRegistering()) {
      return null;
    }
    try {
      this._isDocumentRegistering = true;
      if (!await this._attributesValidate()) {
        return null;
      }
      if (!await this.verifyBeforeRegister()) {
        return null;
      }
      this.$logger.info("一時保存を開始します。");
      const id = await this._registerDocumentAsTemporary(shouldRestrictDocumentNote, shouldValidate);
      if (!id) { return id; }
      await this._printTemporaryRegisteredDocument(this.document.attributes.patient.id, id, this.document.attributes.defaultCopies, hasWatermark);
      await this._launchPostRegistrationApplication(this.document.attributes, id, true, true);
      this._isDocumentRegistered = true;
      return id;
    } catch (e) {
      // 利用側でメッセージを変えたいという要望があったので、メッセージはregisterで表示する役割分担に.
    } finally {
      this._isDocumentRegistering = false;
    }
  }

  private async _printTemporaryRegisteredDocument(patientNo: string, id: number, copies: number, hasWatermark: boolean): Promise<void> {
    try {
      this.$logger.info("一時保存文書(" + patientNo + ", " + id + ", " + copies + ")を印刷します。");
      const repository = DomainRegistry.document.getDocumentRepository();
      const reference = await repository.getDocumentSummary(patientNo, id);
      reference.printSource.hasWatermark = hasWatermark;
      this.$printPdf(reference.printSource.source, copies);
      this.$logger.info("印刷が完了しました。");
    } catch (e) {
      this.$error("印刷エラー", "印刷処理中にエラーが発生しました。", e);
    }
  }

  private async _registerDocumentAsTemporary(shouldRestrictDocumentNote: boolean, shouldValidate: boolean): Promise<number> {
    if (shouldValidate) {
      const isValidateOk = await this.validateEmptyAlertForRegisterAsTemporary();
      if (!isValidateOk) {
        return null;
      }
    }
    try {
      const source = this.document.clone();
      const shouldSkipTemporaryNoteInput = shouldRestrictDocumentNote || !this.$configuration.behavior.document.editor.temporaryNote.shouldShowInputDialogBeforeRegisterAsTemporary;
      const shouldRegister = (shouldSkipTemporaryNoteInput ? true : await this._updateTemporaryNote(source))
        && await this._applyHisInformations(source, EditPhaseKinds.TemporarySave)
        && await this._applyUserDefinitionLinkValue(source)
        && await this._applyCollaborationSupplement(source, true);
      if (!shouldRegister) {
        this.$success("一時保存", "一時保存をキャンセルしました。");
        return null;
      }
      this.applyTimeMeasurementRelatedValues(source);
      return this._executeRegistration(source, true, false);
    } catch (e) {
      // 利用側でメッセージを変えたいという要望があったので、メッセージはregisterで表示する役割分担に.
    }
  }

  private async validateEmptyAlertForRegisterAsTemporary(): Promise<boolean> {
    const containsEmptyAlert = this.validator.containsEmptyAlert();
    if (!containsEmptyAlert) { return true; }

    const kind = this.document.attributes.registerAlertKind;
    if (!kind || kind === RegisterAlertKinds.AllowRegisterAsTemporaryOnly) { return true; }

    // 警告タブを表示させる
    await this.validator.validate(false);

    if (kind === RegisterAlertKinds.AllowRegisterAsTemporaryOnlyWithDialog) {
      const confirmResult =  await this.$commonDialogs.confirmation.confirmCancel(
        null,
        ["入力されていない必須入力項目があります。このまま一時保存してもよろしいですか？"],
        "一時保存する"
      );
      return (confirmResult === 0);
    }
    this.$warn("一時保存中止", "文書に警告があったため一時保存を中止します。警告内容を確認してください。");
    return false;
  }

  private async _updateTemporaryNote(source: Document): Promise<boolean> {
    this.$logger.info("一時保存メモを更新します。");
    const note = await this._inputTemporaryNote(source);
    if (note == null) { // nullの場合はキャンセル.
      this.$logger.info("一時保存メモ入力中にキャンセルされました。");
      return false;
    }
    source.attributes.temporaryNote = note;
    this.$logger.info("一時保存メモを更新しました。");
    return true;
  }

  private async _inputTemporaryNote(source: Document): Promise<string> {
      const result = await this.$commonDialogs.document.noteInput.show(true, source.attributes.temporaryNote, true, "一時保存メモ");
      return result;
  }

  private async _attributesValidate(): Promise<boolean> {
    if (!this.document.attributes.department) {
      this.$warn(this.$wording.common.department + "チェック", this.$wording.common.department + "を選択してください。");
      return false;
    }
    if (this.$configuration.behavior.departmentGroup.feature.isEnable) {
      const currentDepartmentGroup = this.$store.getters.departmentGroup as DepartmentGroup;
      if (!!currentDepartmentGroup
        && Enumerable.from(currentDepartmentGroup.departments).all(d => d.id !== this.document.attributes.department.id)
        && !await this.confirmDepartmentGroupMismatch(this.document.attributes.department.name, currentDepartmentGroup.name)) {
        return false;
      }
    }
    return true;
  }

  private async confirmDepartmentGroupMismatch(departmentName: string, departmentGroupName: string): Promise<boolean> {
    const message = [this.$wording.common.department + "「" + departmentName + "」は" + this.$wording.common.departmentGroup + "「" + departmentGroupName + "」に属していません。", "処理を続行してもよろしいですか？"];
    const result = await this.$commonDialogs.confirmation.confirmDangerCancel(
      null,
      message,
      "処理を続行する"
    );
    return (result === 0);
  }

  private verifyDocumentRegistering(): boolean {
    if (this._isDocumentRegistering) {
      this.$warn("処理中チェック", "文書登録処理中に別の登録処理を開始することはできません。");
      return false;
    }
    if (this._isDocumentRegistered) {
      this.$warn("処理済みチェック", "文書登録完了後に別の登録処理を開始することはできません。");
      return false;
    }
    return true;
  }

  private async verifyBeforeRegister(): Promise<boolean> {
    if (!!this.validator && this.validator.isLinkedExternalScriptProcessing()) {
      this.$warn("処理中チェック", "外部スクリプトを実行中に登録処理を開始することはできません。");
      return false;
    }
    const isScanned = this.document.documentId !== null && this.$configuration.behavior.collaboration.scan.shouldVerifyBeforeEdit
      ? await this.isScanned()
      : false;
    return !isScanned || await this.confirmToScanned() === 0;
  }

  private async isScanned(): Promise<boolean> {
    try {
      const repository = DomainRegistry.collaboration.getCollaborationRepository();
      const isScanned = await repository.isScanned(this.document.attributes.patient.id, this.document.documentId);
      // awaitしないでreturnでPromiseをそのまま返すと、一瞬でfinally抜けてしまうので注意
      this.$logger.debug("isScanned: " + isScanned);
      return isScanned;
    } catch (e) {
      this.$warn("スキャン状態取得失敗", "スキャン取込済みかどうかを取得できませんでした。", e);
      return false;
    } finally {
    }
  }

  private async confirmToScanned(): Promise<number> {
    const updatedMessage = ["記載中に文書がスキャン取込済みになっています。"];
    updatedMessage.push("本当に更新しますか？");

    return this.$commonDialogs.confirmation.confirmDangerCancel(
      null,
      updatedMessage,
      "最新版として更新する"
    );
  }

  private async getLinkedDocumentId(systemKey: string, linkKey: string, patientNo: string): Promise<number> {
    const repository = DomainRegistry.collaboration.getLinkedDocumentRepository();
    return repository.getDocumentId(systemKey, linkKey, patientNo);
  }

  private async confirmToRegisteredLinkedDocument(linkedDocumentId: number, isTemporary: boolean): Promise<MessageBoxCloseAction> {
    const repository = DomainRegistry.document.getDocumentRepository();
    const summary = await repository.getDocumentSummary(this.document.attributes.patient.id, linkedDocumentId);
    const linkedCreatedMessageResult = await this.showLinkedDocumentRegisteredMessage(summary, isTemporary);
    if (linkedCreatedMessageResult !== 0) { return MessageBoxCloseAction.Cancel; }
  }

  private async showLinkedDocumentRegisteredMessage(summary: DocumentSummary, isTemporary: boolean): Promise<number> {
    const registeredMessage  = ["既に紐付いた文書が登録されています。"];
    registeredMessage .push("登録者　：" + summary.createStaffName);
    registeredMessage .push("登録端末：" + summary.createTerminal);
    registeredMessage .push("登録日時：" + Moment(summary.createdDateTime).format("YYYY/MM/DD HH:mm:ss"));
    registeredMessage .push("登録すると本文書が紐付けられます（現在紐付いてる文書の紐付けは外れます）。");
    registeredMessage .push("本当に登録しますか？");
    return this.$commonDialogs.confirmation.confirmRegisterCancel(
      null,
      registeredMessage
    );
  }

  private async getConfirmResult(detail: IDocumentRegistrationResultAlreadyUpdatedDetail, isTemporary: boolean, isSingleEntryFormat: boolean): Promise<MessageBoxCloseAction> {
    if (isSingleEntryFormat) {
      // 競合を回避させたタイミングが重なったときの考慮も必要になるため、重複記載が禁止されたフォーマットでは安全のため登録させない
      await this.showSingeEntryAlertMesssae(detail);
      return MessageBoxCloseAction.Cancel;
    }
    if (detail.isDeleted) {
      // 削除されている
      const deletedMessageResult = await this.showDeletedMessage(detail, isTemporary);
      if (deletedMessageResult === 0) { return MessageBoxCloseAction.New; }
    } else if (detail.isFixed) {
      // 編集不可指定されている
      const fixedMessageResult = await this.showFixedMessage(detail, isTemporary);
      if (fixedMessageResult === 0) { return MessageBoxCloseAction.New; }
    } else if (!detail.isRegisterableRegisteredDocumentAsTemporary) {
      // 一時保存不可
      return this.showRegisteredMessage(detail);
    } else {
      // 更新されている
      const updatedMessageresult = await this.showUpdatedMessage(detail, isTemporary);
      if (updatedMessageresult === 0) { return MessageBoxCloseAction.New; }
      if (updatedMessageresult === 1) { return MessageBoxCloseAction.Update; }
    }
    return MessageBoxCloseAction.Cancel;
  }

  private async showSingeEntryAlertMesssae(detail: IDocumentRegistrationResultAlreadyUpdatedDetail): Promise<void> {
    console.warn(detail);
    await this.$commonDialogs.confirmation.show({
      title: "重複記載",
      message: this.getSingeEntryAlertMesssages(detail),
      buttons: [],
      cancellation: {
        isEnable: true,
        label: "登録を中止する",
      },
    });
  }

  private getSingeEntryAlertMesssages(detail: IDocumentRegistrationResultAlreadyUpdatedDetail): string[] {
    const messages: string[] = [];
    if (detail.isDeleted) {
      messages.push("文書が削除されています。");
    } else if (detail.isFixed) {
      messages.push("文書が編集不可に指定されています。");
    } else {
      messages.push("文書の登録状況が競合しています。");
    }
    messages.push("重複記載が禁止されたフォーマットのため登録処理を進めることができません。");
    return messages;
  }

  private async showUpdatedMessage(detail: IDocumentRegistrationResultAlreadyUpdatedDetail, isTemporary: boolean): Promise<number> {
    const updatedMessage = ["記載中に文書が更新されています。"];
    updatedMessage.push("更新者　：" + detail.updated.userName);
    updatedMessage.push("更新端末：" + detail.updated.terminal);
    updatedMessage.push("更新日時：" + this.toDateTimeText(detail.updated.dateTime));
    updatedMessage.push("本当に更新しますか？");

    return this.$commonDialogs.confirmation.show({
      message: updatedMessage,
      buttons: [
        {
          kind: "confirm",
          label: "新規文書として登録する",
        },
        {
          kind: "danger",
          label: "最新版として更新する",
        }
      ],
      cancellation: {
        isEnable: true,
        label: "登録を中止する",
      },
    });
  }

  private async showDeletedMessage(detail: IDocumentRegistrationResultAlreadyUpdatedDetail, isTemporary: boolean): Promise<number> {
    const registryMode = isTemporary ? "保存" : "登録";
    const deletedMessage = ["記載中に文書が削除されたため、文書を" + registryMode + "できません。"];
    deletedMessage.push("削除者　：" + detail.updated.userName);
    deletedMessage.push("削除端末：" + detail.updated.terminal);
    deletedMessage.push("削除日時：" + this.toDateTimeText(detail.updated.dateTime));
    deletedMessage.push("新規文書として" + registryMode + "しますか？");

    return this.$commonDialogs.confirmation.confirmCancel(
      null,
      deletedMessage,
      "新規文書として登録する"
    );
  }

  private toDateTimeText(date: Date): string {
    if (!date) { return ""; }
    return Moment(date).format("YYYY/MM/DD HH:mm");
  }
  
  private async showFixedMessage(detail: IDocumentRegistrationResultAlreadyUpdatedDetail, isTemporary: boolean): Promise<number> {
    const registryMode = isTemporary ? "保存" : "登録";
    const messages = ["文書が編集不可に指定されているため、文書を" + registryMode + "できません。"];
    // TODO: 余裕があればもうちょっと表示してあげたいところだが・・
    messages.push("新規文書として" + registryMode + "しますか？");

    return this.$commonDialogs.confirmation.confirmCancel(
      null,
      messages,
      "新規文書として登録する"
    );
  }

  private async showRegisteredMessage(detail: IDocumentRegistrationResultAlreadyUpdatedDetail): Promise<number> {
    this.$errorAsHTML(
      "一時保存失敗",
      "<div>記載中に文書が登録されたため、一時保存できません。登録は可能です。</div><div>登録する場合は登録ボタンを押してください。</div>"
    );
    return MessageBoxCloseAction.Cancel;
  }

  private async RetryRegistration(response: MessageBoxCloseAction, source: Document, detail: IDocumentRegistrationResultAlreadyUpdatedDetail, isTemporary: boolean, isPublish: boolean): Promise<number> {
    if (response === MessageBoxCloseAction.New) {
      source.edition = 0;
    } else if (response === MessageBoxCloseAction.Update) {
      source.edition = detail.edition;
    } else {
      return null;
    }
    return this._executeRegistration(source, isTemporary, isPublish);
  }

  private async _launchPostRegistrationApplication(attributes: DocumentAttributes, documentId: number, isTemporary: boolean, withPrinting: boolean): Promise<void> {
    try {
      const launcher = new PostRegistrationApplicationLauncher(this.$configuration.behavior.document.editor.registerer.postRegistration);
      await launcher.launch(this.$store.getters.user, attributes, documentId, isTemporary, withPrinting);
    } catch (e) {
      this.$error("エラー", "文書登録後のアプリケーション起動に失敗しました。", e);
    }
  }
}
